import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EsitoTurno, ParteRisposta } from '@masamune/shared';
import { Icon } from '../ui/Icon';
import { ActionsMenu, type ActionItem } from '../ui/ActionsMenu';
import { Mascotte, type CostumeMascotte, type StatoMascotte } from '../ui/Mascotte';
import { ApiError } from '../../lib/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { toast } from '../../stores/toast.store';
import {
  useAssistenteStore, type AllegatoInChat, type MessaggioChat, type SchedaLetta, type VistaPannello,
} from '../../stores/assistente.store';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { CODICI_MUNE_FERMO, fraseStato, puoRispondere, statoAssistenteQuery } from '../../features/assistente/assistenteApi';
import {
  FRASE_CONNESSIONE_INTERROTTA, eliminaConversazione, inviaDomanda, leggiConversazione, unisciLette,
  type EventoChat,
} from '../../features/assistente/chatApi';
import { AzioneInChat } from '../../features/assistente/AzioneInChat';
import { BarraCarta, CartaConferma } from '../../features/assistente/CartaConferma';
import { CartaMail } from '../../features/assistente/CartaMail';
import { cartaDellUltimaRisposta, senzaTaskCestinate, testoDaLeggereConModifiche, useCartaInBarra } from '../../features/assistente/carteApi';
import { rinfrescaDopoAnnulla, rinfrescaSpesa } from '../../features/assistente/registroApi';
import {
  fendenteLungo, festeggiaAzione, toccaMascotte, useCostume, useMascotteStore, useStatoDentro,
} from '../../features/assistente/mascotteVita';
import { MemoriaVista } from '../../features/assistente/MemoriaVista';
import { AzioniAssistente, AzioniAssistenteMobile } from '../../features/assistente/AzioniAssistente';
import { ConversazioniVista } from '../../features/assistente/ConversazioniVista';
import { ModelloTasto } from '../../features/assistente/ModelloTasto';
import { RiquadroChat } from '../../features/assistente/RiquadroChat';
import { RigheLink } from '../../features/assistente/RigheLink';
import { confermaEliminaConversazione, linkInterno } from '../../features/assistente/riquadriTesto';
import { fileDaIncollare, useAllegatiCampo, useGuardiaFile, useTrascinaFile } from '../../features/assistente/allegati';
import { GraffettaAllegati, RilascioAllegati, StrisciaAllegati } from '../../features/assistente/AllegatiCampo';
import { useVocali } from '../../features/assistente/vocali';
import { BarraRegistrazione, MicrofonoVocale, RigaTrascrivo } from '../../features/assistente/VocaliCampo';
import { ACCETTA_AUDIO, eAudio, PAROLE_VOCALI } from '../../features/assistente/vocaliTesto';
import { AllegatiDomanda, FotoGrande } from '../../features/assistente/AllegatiDomanda';
import {
  PAROLE_ALLEGATI, allegatiDi, fraseDomandaRimessa, fraseFinisco, fraseInviaConErrore,
} from '../../features/assistente/allegatiTesto';
import { MODO_RIVELA } from '../../features/assistente/rivela';
import { useRivelaFlusso } from '../../features/assistente/rivelaFlusso';
import { TestoRivelato } from '../../features/assistente/TestoRivelato';
import '../../features/assistente/assistente.css';

/**
 * Il pannello dell'assistente (28/10/2026). Lorenzo: «in alto a destra che apre un
 * pannello laterale da destra verso sinistra nello stile del chatbot di dash.cloudflare».
 *
 * Sul computer è una terza colonna del guscio (index.css, .os-shell): la pagina si
 * stringe e resta usabile, e il pannello resta aperto mentre si cambia pagina. Sul
 * telefono è a tutto schermo. Lo apre la mascotte in alto a destra (Topbar), o Ctrl+,; le
 * facce le decide features/assistente/mascotteVita.ts.
 *
 * La mascotte del pannello non sta nell'intestazione ma in coda all'ultima risposta,
 * dopo l'ultima parola (Lorenzo, 28/10): mentre lavora è lì da sola con «Sto pensando»
 * e le frasi di quello che fa, poi la risposta compare parola per parola e lei salta fuori
 * alla fine. A pannello vuoto c'è quella grande del saluto.
 *
 * Dal 29/10/2026 (fetta 2B della Fase 2, os-planning/ai/fase-2-progetto.md) la risposta
 * arriva in streaming (chatApi.ts): le parti (testo, riquadri coi numeri, righe con link,
 * avvisi) compaiono nell'ordine in cui arrivano, e la conversazione la salva il server, da
 * riaprire da «Conversazioni» nel menu ⋯.
 *
 * Dalla fetta 2D (29/10/2026) MUNE anche fa: una modifica fatta subito è la riga «Fatto … ·
 * Annulla» (AzioneInChat), una che cancella, manda mail o tocca più cose è la carta «Modifiche
 * da confermare» (CartaConferma), tutte e due nell'ordine in cui arrivano. Al telefono i tasti
 * della carta dell'ultima risposta prendono il posto del campo, in fondo (BarraCarta).
 *
 * Quattro viste: la chat; il foglio «Cosa sa MUNE» di un cliente o progetto (MemoriaVista),
 * che si apre dal menu delle schede o dalla riga «Letto prima di rispondere»; le
 * conversazioni salvate; «Cosa ha fatto MUNE». La chat resta montata (nascosta) mentre si
 * guarda un'altra vista: tornando, è dov'era e niente si ridisegna da capo.
 *
 * Dalla fetta 2F (29/10/2026, os-planning/ai/fase-2f-allegati.md) alla domanda si allegano foto
 * e PDF: la graffetta a sinistra del campo, il trascinamento sul pannello e Ctrl+V al computer,
 * la scelta del telefono. I pezzi stanno nella striscia sopra il campo e si caricano subito, da
 * soli (features/assistente/allegati.ts); la domanda porta solo i loro id e, se un caricamento
 * non è finito, parte quando finisce. Ogni errore prima che la domanda si salvi la rimette nel
 * campo con i suoi allegati. Nel fumetto della domanda le miniature, e un clic apre la foto grande.
 */

/**
 * In che terzo del campo sta il cursore del testo: -1 sinistra, 0 centro, 1 destra.
 * Conta i caratteri della riga in corso su quanti ne stanno in una riga (a 14px circa
 * 7,5px l'uno): basta per sapere dove guardare, non serve la posizione esatta.
 */
function terzoDelCursore(el: HTMLTextAreaElement): number {
  const prima = el.value.slice(0, el.selectionStart ?? el.value.length);
  const riga = prima.split('\n').pop() ?? '';
  const perRiga = Math.max(20, Math.floor(el.clientWidth / 7.5));
  const dove = (riga.length % perRiga) / perRiga;
  return dove < 0.34 ? -1 : dove < 0.67 ? 0 : 1;
}

// Le domande della Fase 2 (29/10/2026): MUNE guarda nel gestionale e risponde coi dati.
const SUGGERIMENTI = [
  'Che task ho in ritardo?',
  'Quanto abbiamo in banca?',
  'Chi ci deve dei soldi?',
];

/** Il server accetta domande fino a 8000 caratteri (chatInput, packages/shared). */
const MAX_MESSAGGIO = 8000;

/** Dopo 8 secondi senza eventi, se il server non ha ancora detto cosa fa, «Ancora un attimo…». */
const ATTESA_LUNGA_MS = 8000;

/** La rete che cade prima della risposta: fetch lancia «Failed to fetch», e a schermo va questa. */
const FRASE_SENZA_RETE = 'MUNE non risponde: controlla la connessione e riprova';

/**
 * Col mouse il cursore può andare nel campo da solo; al telefono no: la tastiera salirebbe e
 * coprirebbe la chat. Lorenzo, 28/10: «deve spuntare solo se clicchi sulla pill per digitare».
 */
const colMouse = () => window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false;

const NESSUNA_PARTE: ParteRisposta[] = [];

/** Il turno in corso: la sua richiesta da interrompere e i due messaggi che scrive. */
interface Turno {
  ctrl: AbortController;
  domandaId: string;
  rispostaId: string;
  // ── 2F, gli allegati (29/10/2026) ──
  /** La domanda com'era scritta: torna nel campo se non si salva. */
  testo: string;
  /** Le foto e i PDF della domanda, aggiornati dal campo mentre finiscono di caricarsi. */
  allegati: AllegatoInChat[];
  /** La domanda è partita verso il server: i caricamenti sono finiti. */
  partita: boolean;
  /** È arrivato `conversazione`: la domanda è salvata, e un errore non la rimette più nel campo. */
  salvata: boolean;
  /** Un `errore` arrivato prima di `conversazione` in una domanda con allegati: la rimette nel campo. */
  errorePrima: { codice: string; frase: string } | null;
}

/**
 * Tiene in vista il punto dove si sta scrivendo quando arriva al bordo basso del pannello.
 * Un elemento più alto del pannello (un riquadro lungo) si ferma col suo inizio in cima:
 * il titolo resta da leggere.
 */
function tieniInVista(el: Element | null, box: HTMLElement | null) {
  if (!el || !box) return;
  const e = el.getBoundingClientRect();
  const b = box.getBoundingClientRect();
  const sotto = e.bottom + 28 - b.bottom;
  if (sotto <= 0) return;
  const sopra = e.top - b.top - 12;
  box.scrollTop += Math.max(0, Math.min(sotto, sopra));
}

/**
 * La mascotte piccola, alta quanto una riga di testo. Si può toccare (toccaMascotte);
 * mentre la risposta compare, il tocco la mostra tutta. Fuori dai lettori di schermo:
 * è un'animazione, non un comando.
 */
function MascottePiccola({ stato, occhi, costume, onTocco }: {
  stato: StatoMascotte;
  occhi: { x: number } | null;
  costume: CostumeMascotte | null;
  onTocco?: () => void;
}) {
  return (
    <button
      type="button"
      className="ast-msc-riga"
      tabIndex={-1}
      aria-hidden="true"
      onClick={(e) => {
        if (onTocco) { onTocco(); return; }
        e.stopPropagation();
        toccaMascotte(e);
      }}
    >
      <Mascotte size={20} stato={stato} occhi={occhi} costume={costume} segui />
    </button>
  );
}

/**
 * Le lettere saltano una dopo l'altra, a scatti: l'onda dei titoli dei giochi a 8 bit.
 * La lancia la mascotte con un saltello (assistente.css, .ast-pensa): le lettere partono
 * due battute dopo di lei. Ogni parola sta insieme e la frase va a capo tra le parole
 * (29/10/2026): le frasi del server sono lunghe («Guardo le fatture di Azienda Agricola
 * Graci…») e su una riga sola si tagliavano di lato.
 */
function Onda({ testo }: { testo: string }) {
  let lettera = 0;
  return (
    <span className="ast-onda" aria-hidden="true">
      {testo.split(/(\s+)/).filter(Boolean).map((pezzo, i) => {
        const inizio = lettera;
        const lettere = Array.from(pezzo);
        lettera += lettere.length;
        if (/^\s+$/.test(pezzo)) return ' ';
        return (
          <span key={i} className="ast-onda-parola">
            {lettere.map((c, j) => (
              <span key={j} style={{ animationDelay: `${(inizio + j + 2) * 55}ms` }}>{c}</span>
            ))}
          </span>
        );
      })}
    </span>
  );
}

interface PropsRisposta {
  m: MessaggioChat;
  /** È l'ultima risposta: la mascotte sta lì, e lo scorrimento la segue. */
  ultima: boolean;
  faccia: StatoMascotte;
  occhi: { x: number } | null;
  costume: CostumeMascotte | null;
  /** Il corpo del pannello che scorre. */
  scorre: React.RefObject<HTMLDivElement>;
  /** Chi legge è in fondo alla chat (non è tornato su a rileggere): lo scorrimento segue la risposta. */
  segui: React.MutableRefObject<boolean>;
  /** Da 8 secondi non arriva niente e il server non ha ancora detto cosa fa. */
  attesaLunga: boolean;
  mobile: boolean;
  onLink: (href: string) => void;
  /** Al telefono, la carta i cui tasti stanno nella barra in fondo al pannello (la carta dell'ultima risposta). */
  cartaInBarra: string | null;
}

/**
 * Una risposta di MUNE. Le parti nell'ordine in cui arrivano, dentro lo stesso blocco:
 * il testo compare parola per parola mentre arriva (rivelaFlusso.ts, con la maniera di
 * rivela.ts), i riquadri coi numeri e le righe con link (features/assistente, RiquadroChat
 * e RigheLink) quando il testo prima di loro è comparso, gli avvisi in grigio o in rosso;
 * dalla 2D anche le modifiche fatte (AzioneInChat) e le carte da confermare (CartaConferma),
 * che compaiono allo stesso modo: quando il testo prima di loro si vede tutto.
 * Il testo ancora da comparire c'è già ma non si vede: le righe sono subito al loro posto.
 * Un clic sul testo (o sulla mascotte, a scatti) mostra subito quello che è arrivato.
 *
 * Mentre lavora, sotto quello che si vede, la riga di stato con la mascotte che pensa:
 * «Sto pensando…», poi le frasi del server («Controllo Qonto…»). Sparisce quando arriva il
 * testo e torna col prossimo `stato`. Una mascotte sola per risposta: finito il turno salta
 * fuori dopo l'ultima parola (o sotto l'ultimo riquadro) e fa la faccia del turno.
 *
 * «Letto prima di rispondere» sta sotto, e compare a risposta finita: sopra, la risposta
 * comincerebbe una riga più in basso di «Sto pensando» e la mascotte salterebbe di riga.
 */
const Risposta = memo(function Risposta({ m, ultima, faccia, occhi, costume, scorre, segui, attesaLunga, mobile, onLink, cartaInBarra }: PropsRisposta) {
  const segnaRivelato = useAssistenteStore((s) => s.segnaRivelato);
  const parti = m.parti ?? NESSUNA_PARTE;
  const inArrivo = !!m.inArrivo;
  const ancora = useRef<HTMLSpanElement>(null);
  const coda = useRef<HTMLDivElement>(null);
  const ultimaParola = useRef<HTMLSpanElement>(null);
  const ultimoPezzo = useRef<HTMLDivElement>(null);
  const riga = useRef<HTMLDivElement>(null);
  const lette = useRef<HTMLDivElement>(null);

  const r = useRivelaFlusso({
    id: m.id,
    parti,
    modo: MODO_RIVELA,
    attivo: !!m.daRivelare,
    inArrivo,
    onFine: () => {
      segnaRivelato(m.id);
      // Finita: la mascotte fa la faccia del turno. Solo se è ancora l'ultima risposta e
      // non è già partita la domanda dopo.
      const st = useAssistenteStore.getState();
      const io = st.messaggi[st.messaggi.length - 1];
      if (st.inAttesa || io?.id !== m.id) return;
      const { recita } = useMascotteStore.getState();
      if (io.parti?.some((x) => x.tipo === 'azione')) festeggiaAzione();
      else if (io.esito === 'completo') recita('dentro', [['contento', 1120]]);
      else if (io.esito === 'errore' || io.esito === 'interrotto') recita('dentro', ['errore']);
    },
    seSparito: () => !useAssistenteStore.getState().messaggi.some((x) => x.id === m.id),
  });
  const aScatti = r.modo === 'scatti';
  const haTesto = parti.some((x) => x.tipo === 'testo');
  const conRiga = inArrivo && r.inPari && (m.stato != null || !haTesto);
  const frase = m.stato ?? (attesaLunga ? 'Ancora un attimo…' : 'Sto pensando…');
  const finita = !inArrivo && !r.scrive;
  // A scatti la mascotte parla mentre il testo si scrive, spinta avanti; negli altri modi
  // salta fuori a turno finito. Con la riga di stato a schermo sta lì, a pensare.
  const parla = aScatti && r.scrive;
  const conMascotte = ultima && !conRiga && (finita || parla);
  const ultimaVisibile = r.partiVisibili - 1;
  // Dopo l'ultima parola (Lorenzo, 28/10), anche quando sotto arrivano le righe con link: sono
  // l'elenco delle cose nominate, non la risposta (integrazione 2B, 29/10/2026). Un riquadro o
  // un avviso dopo il testo invece la portano sotto di loro, in coda.
  let doveFinisce = ultimaVisibile;
  while (doveFinisce > 0 && parti[doveFinisce]?.tipo === 'righe') doveFinisce--;
  const nelTesto = !conMascotte ? null
    : parla ? r.parteInCorso
    : parti[doveFinisce]?.tipo === 'testo' ? doveFinisce : null;
  const entra = r.entra && !aScatti ? 'true' : undefined;
  const mascotte = conMascotte ? (
    <MascottePiccola
      stato={parla ? 'parla' : faccia}
      occhi={parla ? null : occhi}
      costume={costume}
      onTocco={r.scrive ? r.salta : undefined}
    />
  ) : null;

  // Lo scorrimento segue la risposta mentre arriva, se chi legge è in fondo: la riga di
  // stato, la parola che sta comparendo, poi «Letto prima», la mascotte o l'ultimo pezzo.
  // Si misura dopo che il DOM è cambiato e prima del disegno: niente scatti.
  useLayoutEffect(() => {
    if (!ultima || !segui.current) return;
    const meta = riga.current ?? (r.scrive ? ultimaParola.current : null)
      ?? lette.current ?? ancora.current ?? coda.current ?? ultimoPezzo.current;
    tieniInVista(meta, scorre.current);
  });

  return (
    <>
      <div className="ast-risposta">
        {parti.slice(0, r.partiVisibili).map((parte, i) => {
          const rif = i === ultimaVisibile ? ultimoPezzo : undefined;
          switch (parte.tipo) {
            case 'testo': {
              const t = r.testi.get(i);
              if (!t) return null;
              return (
                <div key={i} ref={rif} className="ast-msg" data-ruolo="assistente" onClick={t.r.scrive ? r.salta : undefined}>
                  <TestoRivelato
                    r={t.r}
                    pezzi={t.pezzi}
                    ultimaRef={i === r.parteInCorso ? ultimaParola : undefined}
                    coda={nelTesto === i ? <span ref={ancora} className="ast-ancora" data-entra={entra}>{mascotte}</span> : undefined}
                  />
                </div>
              );
            }
            case 'riquadro':
              return (
                <div key={i} ref={rif} className="ast-parte" data-tipo="riquadro">
                  <RiquadroChat riquadro={parte.riquadro} mobile={mobile} onLink={onLink} />
                </div>
              );
            case 'righe':
              return (
                <div key={i} ref={rif} className="ast-parte" data-tipo="righe">
                  <RigheLink righe={parte.righe} mobile={mobile} onLink={onLink} />
                </div>
              );
            case 'avviso':
              return <div key={i} ref={rif} className="ast-avviso" data-tono={parte.tono}>{parte.testo}</div>;
            // La fetta 2D (29/10/2026): una modifica fatta subito, con l'Annulla, e la carta da
            // confermare. La mascotte va sotto di loro, come sotto un riquadro (ast-coda).
            case 'azione':
              return (
                <div key={i} ref={rif} className="ast-parte" data-tipo="azione">
                  <AzioneInChat azione={parte.azione} mobile={mobile} />
                </div>
              );
            case 'carta':
              return (
                <div key={i} ref={rif} className="ast-parte" data-tipo="carta">
                  <CartaConferma carta={parte.carta} mobile={mobile} tastiFuori={cartaInBarra === parte.carta.id} />
                </div>
              );
            // 2H (29/10/2026): la mail preparata da MUNE, che parte solo col clic su Manda.
            case 'mail':
              return (
                <div key={i} ref={rif} className="ast-parte" data-tipo="mail">
                  <CartaMail carta={parte.mail} mobile={mobile} />
                </div>
              );
            default:
              return null;
          }
        })}
        {conMascotte && nelTesto === null && (
          <div ref={coda} className="ast-coda" data-entra={entra}>{mascotte}</div>
        )}
        {conRiga && (
          <div ref={riga} className="ast-pensa">
            <MascottePiccola stato={faccia} occhi={null} costume={costume} />
            <Onda key={frase} testo={frase} />
          </div>
        )}
      </div>
      {finita && m.lette && <RigaLette lette={m.lette} rif={lette} />}
    </>
  );
});

/**
 * Una risposta che non è arrivata (errore prima del flusso: MUNE spento, budget finito,
 * rete giù): il fumetto rosso, come prima, con la mascotte dopo l'ultima parola.
 */
function Fumetto({ m, ultima, faccia, occhi, costume }: {
  m: MessaggioChat;
  ultima: boolean;
  faccia: StatoMascotte;
  occhi: { x: number } | null;
  costume: CostumeMascotte | null;
}) {
  return (
    <div className="ast-msg" data-ruolo="assistente" data-errore="true">
      {m.testo}
      {ultima && (
        <span className="ast-ancora">
          <MascottePiccola stato={faccia} occhi={occhi} costume={costume} />
        </span>
      )}
    </div>
  );
}

/**
 * La reazione di MUNE sulla domanda, come su WhatsApp (Lorenzo, 28/10): un tondo attaccato
 * all'angolo in basso a sinistra del fumetto. Dalla revisione della 2F (29/10/2026) chi la mette la
 * aggancia al fumetto del testo (.ast-fumetto) o, in una domanda di soli allegati, all'ultimo
 * allegato (AllegatiDomanda): il blocco della domanda è largo quanto la riga degli allegati, e
 * agganciato lì il tondo finiva sotto la prima miniatura, lontano dal fumetto.
 */
function reazioneDi(emoji: string | null | undefined) {
  if (!emoji) return null;
  return (
    <span className="ast-reazione" role="img" aria-label={`Reazione di MUNE: ${emoji}`}>
      {emoji}
    </span>
  );
}

/** «Letto prima di rispondere: scheda di …»: si tocca e apre la scheda. Le schede vuote non si nominano. */
function RigaLette({ lette, rif }: { lette: SchedaLetta[]; rif?: React.Ref<HTMLDivElement> }) {
  const apriMemoria = useAssistenteStore((s) => s.apriMemoria);
  const piene = lette.filter((s) => s.righe > 0);
  if (!piene.length) return null;
  return (
    <div className="ast-lette" ref={rif}>
      Letto prima di rispondere:{' '}
      {piene.map((s, i) => (
        <span key={`${s.tipo}-${s.id}`}>
          {i > 0 && ', '}
          <button className="ast-lette-btn" onClick={() => apriMemoria({ tipo: s.tipo, id: s.id, nome: s.nome })}>
            scheda di {s.nome}
          </button>
        </span>
      ))}
    </div>
  );
}

export function AssistentePanel() {
  const {
    aperto, chiudi, messaggi, aggiungi, aggiornaMessaggio, togliMessaggi, aggiungiParte, reagisci, nuovaConversazione,
    caricaConversazione, setConversazioneId, conversazioneId, conversazione, inAttesa, setInAttesa,
    vista, apriVista, memoria, tornaAllaChat, bozza, svuotaBozza, intero: interoScelto, alternaIntero, esciDaIntero,
  } = useAssistenteStore();
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mobile = useIsMobile();
  // Lo schermo intero vale solo al computer (30/10/2026): al telefono il pannello copre già tutto.
  const intero = interoScelto && !mobile;
  const { data: stato } = useQuery({ ...statoAssistenteQuery, enabled: aperto });
  const faccia = useStatoDentro();
  const costume = useCostume();
  const fendente = useMascotteStore((s) => s.fendente);
  const { recita, setScrive } = useMascotteStore.getState();

  const [testo, setTesto] = useState('');
  // Mentre scrivi la mascotte guarda il punto del campo dove sei: sinistra, centro o destra.
  const [occhiX, setOcchiX] = useState(0);
  // Dopo 8 secondi senza eventi «Sto pensando» diventa «Ancora un attimo».
  const [attesaLunga, setAttesaLunga] = useState(false);
  // Per i lettori di schermo: la risposta arrivata, o l'errore.
  const [annuncio, setAnnuncio] = useState('');
  // La foto aperta grande da un fumetto della domanda (2F, 29/10/2026), con la conversazione in cui
  // si è aperta: cambiando conversazione si chiude, i suoi indirizzi blob: li libera lo store.
  const [fotoGrande, setFotoGrande] = useState<{ allegato: AllegatoInChat; conversazione: number } | null>(null);
  const corpo = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLTextAreaElement>(null);
  // Il piede col campo, e la zona di corpo e piede dove si lasciano i file (2F, 29/10/2026).
  const piede = useRef<HTMLFormElement>(null);
  const zona = useRef<HTMLDivElement>(null);
  const smettiDiScrivere = useRef<number>();
  const attesa = useRef<number>();
  // Dov'era la chat quando si è aperta un'altra vista, e cosa c'era.
  const scrollChat = useRef(0);
  const allUscita = useRef({ messaggi: 0, conversazione: 0 });
  // Lo scorrimento segue la risposta finché chi legge non torna su a rileggere.
  const segui = useRef(true);
  const ultimoScroll = useRef({ top: 0, altezza: 0 });
  const turno = useRef<Turno | null>(null);
  // L'ultima conversazione chiesta da «Conversazioni»: una più vecchia che arriva tardi si butta.
  const apertura = useRef(0);
  // Le foto e i PDF della prossima domanda (2F, 29/10/2026, features/assistente/allegati.ts): vivono
  // nel pannello come il testo del campo, non nella conversazione, e le sue funzioni sono stabili.
  const campoAllegati = useAllegatiCampo();
  // I vocali (2G, 29/10/2026, features/assistente/vocali.ts): il testo trascritto entra in coda al
  // campo e lo manda chi chiede. Col mouse il cursore va in fondo; al telefono la tastiera resta
  // giù. Gli errori vanno nella riga rossa della striscia, come quelli degli allegati.
  const vocali = useVocali({
    onTesto: (t) => {
      setTesto((ora) => (ora.trim() ? `${ora.trimEnd()}\n${t}` : t).slice(0, MAX_MESSAGGIO));
      if (!colMouse()) return;
      window.setTimeout(() => {
        const c = campo.current;
        if (!c || c.disabled) return;
        c.focus();
        c.setSelectionRange(c.value.length, c.value.length);
      }, 30);
    },
    onErrore: (frase) => campoAllegati.avvisa(frase),
  });
  // Per Esc, la chiusura del pannello e l'invio, che leggono lo stato di adesso.
  const vocaliOra = useRef(vocali);
  vocaliOra.current = vocali;

  // Per i lettori di schermo anche quello che dice il campo degli allegati (2F): «Foto "x"
  // allegata», «PDF "x" allegato: 12 pagine», le frasi della riga rossa. La regione è quella che
  // c'è già; l'ultima cosa detta, dal campo o dalla chat, è quella che si legge.
  useEffect(() => {
    if (campoAllegati.annuncio) setAnnuncio(campoAllegati.annuncio);
  }, [campoAllegati.annuncio]);

  // Una vista che non ha niente da mostrare torna la chat.
  const v: VistaPannello = vista === 'memoria' && !memoria ? 'chat' : vista;
  const inMemoria = v === 'memoria' && memoria ? memoria : null;

  // Aperto: il cursore va nel campo, dopo lo scorrimento del pannello. Solo col mouse: al
  // telefono la tastiera salirebbe subito e coprirebbe la mascotte che saluta (Lorenzo,
  // 28/10: «deve spuntare solo se clicchi sulla pill per digitare»).
  useEffect(() => {
    if (!aperto || v !== 'chat') return;
    if (!colMouse()) return;
    const t = window.setTimeout(() => campo.current?.focus(), 260);
    return () => window.clearTimeout(t);
  }, [aperto, v]);

  // «Chiedi a MUNE: …» dalla ricerca: il testo va nel campo e non parte niente (decisione 20).
  useEffect(() => {
    if (bozza === null) return;
    setTesto(bozza.slice(0, MAX_MESSAGGIO));
    svuotaBozza();
    if (!colMouse()) return;
    window.setTimeout(() => {
      const c = campo.current;
      if (!c || c.disabled) return;
      c.focus();
      c.setSelectionRange(c.value.length, c.value.length);
    }, 260);
  }, [bozza, svuotaBozza]);

  // Esc chiude, se sopra non c'è altro (decisione 21). Si ascolta in cattura, prima dei fogli
  // e delle conferme: quando tocca a loro, la loro finestra è ancora nella pagina. Col menu ⋯
  // aperto Esc chiude il menu; con la foto grande aperta chiude la foto (2F, 29/10/2026). A
  // schermo intero Esc riporta il pannello a destra (30/10/2026), e il secondo Esc lo chiude.
  useEffect(() => {
    if (!aperto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const velo = document.querySelector<HTMLElement>('.ast .actions-menu-backdrop');
      if (velo) { velo.click(); return; }
      if (useModalStore.getState().kind || document.querySelector('.modal-backdrop, .m-sheet-root, .cmdk-overlay')) return;
      // Prima un vocale che si sta registrando (2G): Esc lo butta e il pannello resta aperto.
      if (vocaliOra.current.fase !== 'fermo') { vocaliOra.current.annulla(); return; }
      if (document.querySelector('.ast .alg-grande')) { setFotoGrande(null); return; }
      if (intero) { esciDaIntero(); return; }
      chiudi();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [aperto, chiudi, intero, esciDaIntero]);

  // Il pannello chiuso a metà registrazione (2G): il vocale si ferma e si trascrive, e il testo
  // aspetta nel campo. Mentre il browser chiede ancora il permesso, invece, non parte niente.
  useEffect(() => {
    if (aperto) return;
    if (vocaliOra.current.fase === 'registro') vocaliOra.current.ferma();
    else if (vocaliOra.current.fase === 'chiedo') vocaliOra.current.annulla();
  }, [aperto]);

  // In fondo alla chat a ogni messaggio nuovo, quando parte un turno e quando si apre
  // un'altra conversazione. La risposta che sta comparendo scorre per conto suo (Risposta):
  // qui si andrebbe in fondo, nel bianco del testo che ancora non si vede.
  useEffect(() => {
    if (!inAttesa && messaggi[messaggi.length - 1]?.daRivelare) return;
    const c = corpo.current;
    if (c) c.scrollTop = c.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messaggi.length, inAttesa, conversazione]);

  // Tornando da un'altra vista la chat è dov'era; se nel frattempo è arrivato qualcosa, o
  // si è aperta un'altra conversazione, in fondo. Uscendo dalla chat la foto grande si chiude (2F):
  // tornando non deve riaprirsi da sola.
  useLayoutEffect(() => {
    const c = corpo.current;
    if (!c) return;
    const st = useAssistenteStore.getState();
    if (v !== 'chat') {
      allUscita.current = { messaggi: st.messaggi.length, conversazione: st.conversazione };
      setFotoGrande(null);
      return;
    }
    const cambiata = st.messaggi.length !== allUscita.current.messaggi || st.conversazione !== allUscita.current.conversazione;
    c.scrollTop = cambiata ? c.scrollHeight : scrollChat.current;
  }, [v]);

  // Un'altra conversazione (nuova o aperta da «Conversazioni»): il turno di prima si
  // interrompe, così la domanda subito dopo non prende 409 e niente arriva tardi.
  useEffect(() => () => {
    const t = turno.current;
    turno.current = null;
    t?.ctrl.abort();
    window.clearTimeout(attesa.current);
    // 2F, dalla revisione (29/10/2026): una domanda con allegati interrotta prima di salvarsi non
    // torna più nel campo. Il campo la dimentica: senza, lo stesso file allegato di nuovo nella
    // conversazione nuova trovava il suo gemello nella domanda abbandonata e spariva senza frase.
    // `abbandona` è una funzione fissa del campo: leggerla da questo giro va bene.
    if (t && t.allegati.length > 0 && !t.salvata) campoAllegati.abbandona();
    // Pannello smontato a metà risposta (un ruolo di prova, il login rifatto): il turno si
    // chiude anche nello store, come in una conversazione riaperta, se no al ritorno la chat
    // resta su «Sto pensando…». Cambiando conversazione inAttesa è già falso.
    const st = useAssistenteStore.getState();
    if (t && st.inAttesa) {
      st.aggiungiParte(t.rispostaId, { tipo: 'avviso', testo: 'Risposta interrotta: riprova', tono: 'alert' });
      st.aggiornaMessaggio(t.rispostaId, { inArrivo: false, stato: null, esito: 'interrotto' });
      st.setInAttesa(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversazione]);

  useEffect(() => () => window.clearTimeout(smettiDiScrivere.current), []);

  /** Il conto degli 8 secondi riparte a ogni evento. */
  const ripartiAttesa = () => {
    window.clearTimeout(attesa.current);
    attesa.current = window.setTimeout(() => setAttesaLunga(true), ATTESA_LUNGA_MS);
  };

  /** Fine del turno: la risposta resta com'è, la chat si può usare di nuovo. */
  const chiudiTurno = (t: Turno, esito: EsitoTurno | null) => {
    turno.current = null;
    window.clearTimeout(attesa.current);
    setAttesaLunga(false);
    aggiornaMessaggio(t.rispostaId, { inArrivo: false, stato: null, ...(esito ? { esito } : {}) });
    setInAttesa(false);
    const m = useAssistenteStore.getState().messaggi.find((x) => x.id === t.rispostaId);
    // Con la frase delle modifiche fatte e il titolo della carta da confermare (2D).
    setAnnuncio(testoDaLeggereConModifiche(m?.parti ?? NESSUNA_PARTE));
    qc.invalidateQueries({ queryKey: ['ai', 'conversazioni'] });
    // La spesa di oggi è cambiata: Impostazioni la rilegge, e fraseStato sa se si è arrivati al tetto.
    rinfrescaSpesa(qc);
  };

  /** Un evento del flusso, nel turno `t` (chatApi.ts, inviaDomanda). */
  const evento = (t: Turno, e: EventoChat) => {
    switch (e.nome) {
      case 'conversazione':
        setConversazioneId(e.dati.id);
        aggiornaMessaggio(t.domandaId, { serverId: e.dati.domandaId });
        aggiornaMessaggio(t.rispostaId, { serverId: e.dati.domandaId });
        // Una conversazione nuova compare subito nella barra dello schermo intero (30/10/2026).
        if (e.dati.nuova) qc.invalidateQueries({ queryKey: ['ai', 'conversazioni'] });
        return;
      case 'avviso':
        aggiungiParte(t.rispostaId, { tipo: 'avviso', testo: e.dati.testo, tono: e.dati.tono });
        return;
      case 'letto': {
        const prima = useAssistenteStore.getState().messaggi.find((x) => x.id === t.rispostaId)?.lette;
        aggiornaMessaggio(t.rispostaId, { lette: unisciLette(prima, e.dati.schede) });
        return;
      }
      case 'stato':
        aggiornaMessaggio(t.rispostaId, { stato: e.dati.frase });
        return;
      case 'riquadro':
        aggiungiParte(t.rispostaId, { tipo: 'riquadro', riquadro: e.dati.riquadro });
        return;
      case 'testo':
        // Arriva il testo: la riga di stato sparisce, e torna col prossimo `stato`.
        aggiungiParte(t.rispostaId, { tipo: 'testo', testo: e.dati.testo });
        aggiornaMessaggio(t.rispostaId, { stato: null });
        return;
      case 'reazione':
        // Sotto il fumetto della domanda, come su WhatsApp.
        reagisci(t.domandaId, e.dati.emoji);
        return;
      case 'righe': {
        // Senza le task create e già tornate nel cestino con l'Annulla, mentre MUNE scriveva
        // (29/10/2026, dalla revisione della 2D): il link si aprirebbe su «Task non trovato».
        const righe = senzaTaskCestinate(e.dati.righe);
        if (righe.length) aggiungiParte(t.rispostaId, { tipo: 'righe', righe });
        return;
      }
      case 'azione':
        aggiungiParte(t.rispostaId, { tipo: 'azione', azione: e.dati.azione });
        // «Preparo la task…» non resta sotto «Fatto … · Annulla» (29/10/2026, dalla revisione
        // della 2D): fino al testo del giro dopo torna «Sto pensando…», come dopo un riquadro.
        aggiornaMessaggio(t.rispostaId, { stato: null });
        // La modifica è già nel gestionale (integrazione 2D, 29/10/2026): la pagina aperta sotto
        // il pannello (Task, scheda del cliente, trattativa) si rilegge da sola, come dopo un Annulla.
        rinfrescaDopoAnnulla(qc);
        return;
      case 'conferma': {
        // A inizio turno il server rimanda le carte della conversazione ancora da decidere (dalla
        // revisione delle correzioni della 2D, 29/10/2026): una connessione caduta poteva lasciarne
        // una che la chat non aveva mai mostrato. Quelle che la chat ha già non si ripetono.
        const id = e.dati.carta.id;
        const giaVista = useAssistenteStore.getState().messaggi.some((m) => m.parti?.some((p) => p.tipo === 'carta' && p.carta?.id === id));
        if (giaVista) return;
        aggiungiParte(t.rispostaId, { tipo: 'carta', carta: e.dati.carta });
        aggiornaMessaggio(t.rispostaId, { stato: null });
        return;
      }
      case 'mail': {
        // 2H (29/10/2026): come `conferma`, anche le carte delle mail ancora da decidere tornano a
        // inizio turno; quelle che la chat ha già non si ripetono.
        const id = e.dati.mail.id;
        const giaVista = useAssistenteStore.getState().messaggi.some((m) => m.parti?.some((p) => p.tipo === 'mail' && p.mail?.id === id));
        if (giaVista) return;
        aggiungiParte(t.rispostaId, { tipo: 'mail', mail: e.dati.mail });
        aggiornaMessaggio(t.rispostaId, { stato: null });
        return;
      }
      case 'allegati': {
        // Gli allegati che MUNE non ha letto (2F, 29/10/2026): sono usciti dalla conversazione, e
        // nel fumetto della domanda restano senza miniatura, con «Non letto». La frase arriva con
        // l'`errore` subito dopo.
        const nonLetti = new Set(e.dati.nonLetti);
        const domanda = useAssistenteStore.getState().messaggi.find((x) => x.id === t.domandaId);
        if (!domanda?.allegati?.length) return;
        aggiornaMessaggio(t.domandaId, {
          allegati: domanda.allegati.map((a) => (a.id && nonLetti.has(a.id) ? { ...a, letto: false } : a)),
        });
        return;
      }
      case 'errore':
        // A flusso partito restano le parti arrivate e sotto, in rosso, la frase dell'errore.
        aggiungiParte(t.rispostaId, { tipo: 'avviso', testo: e.dati.frase, tono: 'alert' });
        aggiornaMessaggio(t.rispostaId, { esito: 'errore' });
        if (CODICI_MUNE_FERMO.has(e.dati.codice)) qc.invalidateQueries({ queryKey: ['ai', 'stato'] });
        return;
      case 'fine':
        chiudiTurno(t, e.dati.esito);
        return;
    }
  };

  /**
   * La domanda torna nel campo (2F, 29/10/2026, «Domanda rimessa nel campo»). Con degli allegati,
   * un errore che arriva prima di `conversazione` vuol dire che la domanda non si è salvata e che i
   * file sono ancora sul server, in attesa, e valgono: i due fumetti spariscono (senza liberare gli
   * indirizzi delle foto, che tornano nella striscia), il testo torna nel campo prima di quello
   * scritto nel frattempo, i pezzi tornano nella striscia e la frase va nella riga rossa. Il bordo
   * rosso solo sui pezzi che l'errore nomina (`sbagliati`); con ALLEGATI_CONVERSAZIONE (`codice`)
   * accanto c'è «Nuova conversazione». Senza, dopo un errore gli allegati sparivano dal campo
   * restando in attesa sul server.
   */
  const rimettiNelCampo = (t: Turno, frase: string, sbagliati: string[], codice: string | null) => {
    turno.current = null;
    window.clearTimeout(attesa.current);
    setAttesaLunga(false);
    togliMessaggi([t.domandaId, t.rispostaId]);
    setInAttesa(false);
    if (t.testo) setTesto((ora) => (ora.trim() ? `${t.testo}\n${ora}` : t.testo).slice(0, MAX_MESSAGGIO));
    campoAllegati.rimetti(t.allegati, frase, sbagliati, codice);
    setAnnuncio(frase);
    recita('dentro', ['errore']);
  };

  /**
   * Il turno si è fermato per un errore: prima del flusso il fumetto rosso, dopo un avviso sotto le
   * parti arrivate. Con degli allegati (2F) ogni errore prima di `conversazione` rimette invece la
   * domanda nel campo: il fumetto rosso resta per le domande senza allegati.
   */
  const fallito = (t: Turno, e: unknown, partito: boolean) => {
    if (e instanceof ApiError && CODICI_MUNE_FERMO.has(e.code)) qc.invalidateQueries({ queryKey: ['ai', 'stato'] });
    if (t.allegati.length > 0 && !t.salvata) {
      const prima = t.errorePrima;
      // Le frasi col nome del pezzo le sceglie allegatiTesto.ts dal codice e da `details`.
      if (prima) rimettiNelCampo(t, fraseDomandaRimessa(prima, t.allegati), [], prima.codice);
      else if (e instanceof ApiError) {
        const frase = fraseDomandaRimessa({ codice: e.code, frase: e.message, details: e.details }, t.allegati);
        rimettiNelCampo(t, frase, allegatiDi(e.details), e.code);
      } else rimettiNelCampo(t, FRASE_SENZA_RETE, [], null);
      return;
    }
    if (partito) {
      aggiungiParte(t.rispostaId, { tipo: 'avviso', testo: e instanceof ApiError ? e.message : FRASE_CONNESSIONE_INTERROTTA, tono: 'alert' });
      chiudiTurno(t, 'errore');
      return;
    }
    // Le frasi del server sono già in italiano; una rete che cade dà «Failed to fetch».
    const frase = e instanceof ApiError ? e.message : FRASE_SENZA_RETE;
    turno.current = null;
    window.clearTimeout(attesa.current);
    setAttesaLunga(false);
    aggiornaMessaggio(t.rispostaId, {
      testo: frase, errore: true, parti: [], inArrivo: false, stato: null, daRivelare: false, esito: 'errore',
    });
    setInAttesa(false);
    setAnnuncio(frase);
    recita('dentro', ['errore']);
  };

  const fermo = stato ? fraseStato(stato) : null;
  const pronto = puoRispondere(stato) && !fermo;
  // Il microfono c'è solo coi vocali accesi sul server (la chiave di Groq) e MUNE che può rispondere.
  const conVocali = !!stato?.vocali && pronto;
  // Finché lo stato non arriva il campo si può già usare.
  const caricando = stato === undefined;

  /** Manda la domanda, con gli id degli allegati già sul server, e segue il flusso della risposta. */
  const manda = (questo: Turno, conversazioneId: string | null, pagina: string, allegati: string[]) => {
    const vivo = () => turno.current === questo;
    let partito = false;
    inviaDomanda({
      conversazioneId,
      testo: questo.testo,
      allegati,
      pagina,
      signal: questo.ctrl.signal,
      onEvento: (nome, dati) => {
        if (!vivo()) return;
        partito = true;
        ripartiAttesa();
        const e = { nome, dati } as EventoChat;
        if (e.nome === 'conversazione') questo.salvata = true;
        // Un `errore` prima di `conversazione`: la domanda non si è salvata (un 529 al primo
        // conteggio, un allegato sparito proprio al salvataggio). Con degli allegati la domanda
        // torna nel campo alla `fine` che lo segue, a turno chiuso anche sul server: rifatta
        // subito non prende «Sto ancora rispondendo alla domanda di prima» (2F, 29/10/2026).
        if (!questo.salvata && questo.allegati.length > 0) {
          if (e.nome === 'errore') {
            questo.errorePrima = e.dati;
            if (CODICI_MUNE_FERMO.has(e.dati.codice)) qc.invalidateQueries({ queryKey: ['ai', 'stato'] });
            return;
          }
          const prima = questo.errorePrima;
          if (e.nome === 'fine' && prima) {
            rimettiNelCampo(questo, fraseDomandaRimessa(prima, questo.allegati), [], prima.codice);
            return;
          }
        }
        evento(questo, e);
      },
    })
      .catch((e: unknown) => { if (vivo()) fallito(questo, e, partito); })
      // Senza `fine` e senza errore (non dovrebbe succedere): il turno si chiude lo stesso.
      .finally(() => { if (vivo()) chiudiTurno(questo, null); });
  };

  const invia = () => {
    const t = testo.trim();
    const nelCampo = campoAllegati.allegati;
    // Lo stato di adesso e non quello del disegno: due Invio di fila non mandano due domande.
    // Dalla 2F (29/10/2026) una domanda può essere fatta di soli allegati.
    if ((!t && !nelCampo.length) || useAssistenteStore.getState().inAttesa || !pronto) return;
    // Un vocale che si registra o si trascrive (2G): la domanda aspetta il suo testo.
    if (vocaliOra.current.fase !== 'fermo' || vocaliOra.current.inCoda > 0) return;
    // Un file fermo per la rete: la domanda non parte finché non lo si toglie o lo si riprova.
    const fermoInRete = nelCampo.find((a) => a.stato === 'errore');
    if (fermoInRete) {
      campoAllegati.avvisa(fraseInviaConErrore(fermoInRete.nome));
      return;
    }
    const conversazioneDiPrima = useAssistenteStore.getState().conversazioneId;
    const pagina = location.pathname;
    const questo: Turno = {
      ctrl: new AbortController(), domandaId: '', rispostaId: '',
      testo: t, allegati: [], partita: false, salvata: false, errorePrima: null,
    };
    // All'invio la striscia si svuota e i pezzi passano al fumetto della domanda coi loro indirizzi
    // blob:. Quelli ancora in viaggio finiscono di caricarsi e aggiornano la loro barra e la riga
    // di stato («Finisco di caricare il PDF…») finché la domanda non parte.
    let finisco: string | null = null;
    const consegnati = nelCampo.length
      ? campoAllegati.consegna((pezzi) => {
          questo.allegati = pezzi;
          if (!questo.domandaId || turno.current !== questo) return;
          aggiornaMessaggio(questo.domandaId, { allegati: pezzi });
          const ancora = fraseFinisco(pezzi);
          if (!questo.partita && ancora && ancora !== finisco) {
            finisco = ancora;
            aggiornaMessaggio(questo.rispostaId, { stato: ancora });
          }
        })
      : null;
    if (consegnati) questo.allegati = consegnati.allegati;
    finisco = fraseFinisco(questo.allegati);
    questo.domandaId = aggiungi({ ruolo: 'utente', testo: t, ...(questo.allegati.length ? { allegati: questo.allegati } : {}) });
    questo.rispostaId = aggiungi({ ruolo: 'assistente', testo: '', parti: [], inArrivo: true, stato: finisco, daRivelare: true });
    setTesto('');
    setScrive(false);
    // Al telefono il campo perde il fuoco: la tastiera si chiude e la risposta col riquadro
    // si vede intera (con la tastiera aperta alla chat restano poco più di 300 px).
    if (!colMouse()) campo.current?.blur();
    // Easter egg: chi scrive «katana» si prende un fendente lungo tutto il pannello.
    if (/\bkatana\b/i.test(t)) fendenteLungo();
    segui.current = true;
    setAttesaLunga(false);
    ripartiAttesa();
    setAnnuncio('');
    setInAttesa(true);
    turno.current = questo;
    if (!consegnati) {
      manda(questo, conversazioneDiPrima, pagina, []);
      return;
    }
    // La domanda parte quando i file sono tutti sul server: è il seguito della promessa del clic,
    // mai un effetto che guarda «tutti pronti» (la chat costa, e in locale StrictMode la farebbe
    // partire due volte). Finito l'ultimo caricamento la riga torna «Sto pensando…»: senza, mentre
    // il server conta i PDF direbbe ancora «Finisco di caricare il PDF…». Un caricamento che non
    // arriva rimette la domanda nel campo, con la frase del file.
    void consegnati.pronti.then((esito) => {
      if (turno.current !== questo) return;
      if (!esito.ok) {
        rimettiNelCampo(questo, esito.frase, [], null);
        return;
      }
      questo.partita = true;
      aggiornaMessaggio(questo.rispostaId, { stato: null });
      setAttesaLunga(false);
      ripartiAttesa();
      manda(questo, conversazioneDiPrima, pagina, esito.ids);
    });
  };

  const nuova = () => {
    nuovaConversazione();
    segui.current = true;
    recita('dentro', ['saluta']);
  };

  /**
   * I file scelti con la graffetta (2F, 29/10/2026): entrano nella striscia. Col mouse il cursore
   * torna nel campo; al telefono no, la tastiera resta giù e si vede la striscia (Lorenzo, 28/10).
   */
  const sceltiFile = (file: File[]) => {
    const altri = smista(file);
    if (altri.length) campoAllegati.aggiungi(altri, 'scelta');
    if (colMouse()) campo.current?.focus();
  };

  /**
   * I vocali tra i file scelti, lasciati o incollati (2G, 29/10/2026): vanno a trascrivere e il loro
   * testo entra nel campo; il resto va nella striscia. Coi vocali spenti tutto alla striscia, che
   * dice il tipo che non va.
   */
  const smista = (file: File[]): File[] => {
    if (!conVocali) return file;
    const audio = file.filter(eAudio);
    if (audio.length) vocali.trascriviFile(audio);
    return file.filter((f) => !eAudio(f));
  };

  /**
   * I file lasciati sul pannello (2F, solo al computer): entrano nella striscia anche da un'altra
   * vista (memoria, conversazioni, azioni), che torna la chat; la mascotte si stupisce.
   */
  const lasciatiFile = (file: File[]) => {
    if (useAssistenteStore.getState().vista !== 'chat') tornaAllaChat();
    const altri = smista(file);
    if (altri.length) campoAllegati.aggiungi(altri, 'trascina');
    recita('dentro', ['stupito']);
    if (colMouse()) campo.current?.focus();
  };

  /**
   * Un clic su una foto del fumetto della domanda (2F): si apre grande nel pannello. I PDF, le
   * foto non lette e quelle senza miniatura (la loro riga non c'è più) non si aprono.
   */
  const apriFotoGrande = (a: AllegatoInChat) => {
    if (a.tipo !== 'foto' || !a.letto || !a.anteprima) return;
    setFotoGrande({ allegato: a, conversazione: useAssistenteStore.getState().conversazione });
  };
  const chiudiFotoGrande = () => setFotoGrande(null);
  // Solo nella chat della conversazione in cui si è aperta: cambiando conversazione lo store libera
  // gli indirizzi delle sue foto.
  const fotoAperta = fotoGrande && v === 'chat' && fotoGrande.conversazione === conversazione ? fotoGrande.allegato : null;

  // Trascinare foto e PDF sul pannello: al computer, con MUNE che può rispondere. A pannello aperto
  // un file lasciato fuori dalla zona non si apre al posto dell'app (la guardia della finestra).
  const trascina = useTrascinaFile(zona, aperto && !mobile && (pronto || caricando), lasciatiFile);
  useGuardiaFile(aperto && !mobile);

  /**
   * Dai link dei riquadri, delle righe e del registro: solo pagine del gestionale. Al telefono
   * il pannello copre tutto: si chiude e si apre la pagina, e riaprendolo la conversazione è
   * dov'era. Sul computer resta aperto accanto alla pagina; da schermo intero torna a destra,
   * così la pagina si vede (30/10/2026).
   */
  const apriLink = useCallback((href: string) => {
    const pagina = linkInterno(href);
    if (!pagina) return;
    if (mobile) chiudi();
    else if (intero) esciDaIntero();
    navigate(pagina);
  }, [mobile, intero, chiudi, esciDaIntero, navigate]);

  /**
   * «Conversazioni»: si apre nella chat e si continua. Il turno in corso si interrompe. Quella già
   * aperta torna alla chat solo mentre MUNE ci sta scrivendo; se no si rilegge come le altre (dalla
   * revisione delle correzioni della 2D, 29/10/2026): dopo una connessione caduta il server può
   * avere una carta o un esito che la chat non ha. `daBarra`: dalla barra dello schermo intero
   * (30/10/2026), che sta accanto alla chat: lì la vista resta la chat mentre si carica.
   */
  const apriConversazione = (id: string, daBarra = false) => {
    const st = useAssistenteStore.getState();
    if (id === st.conversazioneId && st.inAttesa) { apriVista('chat'); return; }
    const mia = ++apertura.current;
    leggiConversazione(id)
      .then((c) => {
        if (mia !== apertura.current) return;
        if (!daBarra && useAssistenteStore.getState().vista !== 'conversazioni') return;
        segui.current = true;
        caricaConversazione(c);
      })
      .catch((e: unknown) => {
        if (mia !== apertura.current) return;
        if (e instanceof ApiError && e.status === 404) qc.invalidateQueries({ queryKey: ['ai', 'conversazioni'] });
        toast.alert('Conversazione non aperta', e instanceof ApiError ? e.message : 'Riprova tra un momento');
      });
  };

  /** Una conversazione eliminata (da «Conversazioni» o dal menu): se era quella aperta, se ne comincia una nuova. */
  const eliminata = (id: string) => {
    qc.invalidateQueries({ queryKey: ['ai', 'conversazioni'] });
    if (id === useAssistenteStore.getState().conversazioneId) nuovaConversazione();
  };

  /** «Elimina questa conversazione» del menu ⋯: la stessa conferma della vista «Conversazioni». */
  const eliminaQuesta = async () => {
    const id = useAssistenteStore.getState().conversazioneId;
    if (!id) return;
    if (!(await confermaEliminaConversazione())) return;
    try {
      await eliminaConversazione(id);
    } catch (e) {
      // Già sparita (la pulizia della notte, un'altra scheda): per chi guarda è eliminata lo stesso.
      if (!(e instanceof ApiError && e.status === 404)) {
        toast.alert('Conversazione non eliminata', e instanceof ApiError ? e.message : 'Riprova tra un momento');
        return;
      }
    }
    eliminata(id);
  };

  const vuota = messaggi.length === 0;
  const occhi = useMemo(() => (faccia === 'ascolta' ? { x: occhiX } : null), [faccia, occhiX]);
  const ultimo = messaggi[messaggi.length - 1];

  // La carta dell'ultima risposta (2D, 29/10/2026). Al telefono i suoi tasti stanno nella barra
  // in fondo, al posto del campo, sotto il pollice: la barra compare a turno finito, quando la
  // carta si vede tutta, e resta finché è da decidere (non scaduta, non usata altrove). Nella
  // chat la carta non mostra i suoi tasti nemmeno prima, così non saltano dalla carta alla barra.
  const cartaUltima = mobile ? cartaDellUltimaRisposta(messaggi) : null;
  const cartaInBarra = useCartaInBarra(
    cartaUltima,
    v === 'chat' && !inAttesa && !!ultimo && !ultimo.inArrivo && !ultimo.daRivelare,
  );
  const conBarra = !!cartaInBarra;

  // La barra compare a tastiera chiusa: se il campo aveva il fuoco (si stava già scrivendo la
  // domanda dopo) lo perde, e il testo scritto resta per dopo. La chat va in fondo, se chi legge
  // era lì: la barra è più alta del campo e coprirebbe le ultime righe della carta.
  useLayoutEffect(() => {
    if (!conBarra) return;
    if (document.activeElement === campo.current) campo.current?.blur();
    const c = corpo.current;
    if (c && segui.current) c.scrollTop = c.scrollHeight;
  }, [conBarra]);

  // Il piede che cresce (2F, 29/10/2026): la striscia degli allegati che compare, la riga rossa,
  // la domanda rimessa nel campo, il campo che va a capo. Il corpo è flessibile e si accorcia, e
  // l'ultima riga con la mascotte dopo l'ultima parola uscirebbe di vista: se chi legge era in
  // fondo, la chat scorre di quanto il piede è cresciuto, come per la barra della carta (sopra).
  // Di quanto e non fino in fondo: mentre la risposta compare, sotto c'è il testo che ancora non
  // si vede. Quando il piede si accorcia il corpo si allunga e il browser resta in fondo da sé.
  useEffect(() => {
    const f = piede.current;
    if (!f || typeof ResizeObserver === 'undefined') return;
    let alto = f.offsetHeight;
    const ro = new ResizeObserver(() => {
      const h = f.offsetHeight;
      const di = h - alto;
      alto = h;
      const c = corpo.current;
      if (di > 0 && c && segui.current) c.scrollTop += di;
    });
    ro.observe(f);
    return () => ro.disconnect();
  }, []);

  // Il menu ⋯. Al telefono anche «Nuova conversazione», che sul computer è il tasto + accanto.
  const voci: ActionItem[] = [
    { key: 'nuova', label: 'Nuova conversazione', icon: 'plus', onClick: nuova, hidden: !mobile || vuota },
    // A schermo intero le conversazioni stanno già nella barra a sinistra.
    { key: 'conversazioni', label: 'Conversazioni', icon: 'list', onClick: () => apriVista('conversazioni'), hidden: intero },
    { key: 'azioni', label: 'Cosa ha fatto MUNE', icon: 'undo', onClick: () => apriVista('azioni') },
    {
      key: 'elimina', label: 'Elimina questa conversazione', icon: 'trash', danger: true,
      // Solo con una conversazione salvata aperta, e non mentre MUNE ci sta scrivendo.
      hidden: !conversazioneId, disabled: inAttesa,
      onClick: () => { void eliminaQuesta(); },
    },
  ];

  const titolo = inMemoria ? 'Cosa sa MUNE'
    : v === 'conversazioni' ? 'Conversazioni'
    : v === 'azioni' ? 'Cosa ha fatto MUNE'
    : 'MUNE';
  // «Restano 30 giorni» sotto il titolo al computer; al telefono lo dice la nota sotto l'elenco
  // (ConversazioniVista), e l'intestazione resta col titolo solo.
  const sotto = inMemoria ? inMemoria.nome
    : v === 'conversazioni' ? (mobile ? null : 'Restano 30 giorni')
    : v === 'azioni' ? null
    : fermo ?? (stato?.stato === 'finta' ? 'In prova: risposte finte' : 'Pronto');

  // Per i lettori di schermo: la frase della riga di stato mentre lavora, a fine turno il testo intero.
  const daAnnunciare = inAttesa && ultimo?.inArrivo
    ? (ultimo.stato ?? (attesaLunga ? 'Ancora un attimo' : 'Sto pensando'))
    : annuncio;

  return (
    <aside
      className="ast"
      data-open={aperto}
      data-intero={intero ? 'true' : undefined}
      aria-label="MUNE"
      aria-hidden={!aperto}
      // Da chiuso non si raggiunge col tab.
      {...(!aperto ? { inert: '' as unknown as boolean } : {})}
    >
      <div className="ast-in">
        {/* A schermo intero (30/10/2026), come ChatGPT: a sinistra «Nuova conversazione» e le
            conversazioni degli ultimi 30 giorni; un clic la apre nella chat, al centro. */}
        {intero && (
          <nav className="ast-rail" aria-label="Conversazioni">
            <div className="ast-rail-su">
              <button type="button" className="ast-rail-nuova" onClick={nuova}>
                <Icon name="plus" size={14} /> Nuova conversazione
              </button>
            </div>
            <div className="ast-rail-hd">
              <span className="ast-rail-titolo">Conversazioni</span>
              <span className="ast-rail-nota">Restano 30 giorni</span>
            </div>
            <div className="ast-rail-lista">
              <ConversazioniVista attuale={conversazioneId} mobile={false} onApri={(id) => apriConversazione(id, true)} onEliminata={eliminata} />
            </div>
          </nav>
        )}
        <header className="ast-hd">
          <div className="ast-hd-testo">
            <div className="ast-titolo-riga">
              <div className="ast-titolo">{titolo}</div>
              {/* Il modello della chat, Haiku o Sonnet (30/10/2026): un clic passa all'altro. */}
              {v === 'chat' && stato?.stato === 'attiva' && <ModelloTasto stato={stato} occupato={inAttesa} />}
            </div>
            {sotto && <div className="ast-sotto" title={sotto}>{sotto}</div>}
          </div>
          {v !== 'chat' ? (
            <button className="btn btn-ghost btn-icon" onClick={tornaAllaChat} title="Torna alla chat" aria-label="Torna alla chat">
              <Icon name="message" size={14} />
            </button>
          ) : (
            <>
              {!mobile && !vuota && !intero && (
                <button className="btn btn-ghost btn-icon" onClick={nuova} title="Nuova conversazione" aria-label="Nuova conversazione">
                  <Icon name="plus" size={14} />
                </button>
              )}
              <ActionsMenu items={voci} label="Altro" />
            </>
          )}
          {/* Schermo intero e ritorno al pannello: solo al computer (30/10/2026). */}
          {!mobile && (
            <button
              className="btn btn-ghost btn-icon"
              onClick={alternaIntero}
              title={intero ? 'Torna al pannello (Esc)' : 'Schermo intero'}
              aria-label={intero ? 'Riporta MUNE nel pannello' : 'MUNE a schermo intero'}
            >
              <Icon name={intero ? 'minimize' : 'maximize'} size={14} />
            </button>
          )}
          <button className="btn btn-ghost btn-icon" onClick={chiudi} title={intero ? 'Chiudi' : 'Chiudi (Esc)'} aria-label="Chiudi MUNE">
            <Icon name="x" size={14} />
          </button>
        </header>

        {/* Il fendente lungo tutto il pannello (easter egg «katana»): sta fuori dalla chat
            che scorre, così si vede anche a conversazione lunga. Riparte grazie alla key. */}
        {fendente > 0 && v === 'chat' && <div key={fendente} className="ast-fendente" aria-hidden="true" />}

        {/* Corpo e piede insieme (2F, 29/10/2026): la zona dove si lasciano i file trascinati. Il
            riquadro di rilascio e la foto grande si misurano su .ast-in (allegati.css): al
            computer coprono corpo e piede, l'intestazione resta scoperta. */}
        <div className="ast-zona" ref={zona}>
          {inMemoria && (
            <div className="ast-bd">
              <MemoriaVista key={`${inMemoria.tipo}-${inMemoria.id}`} bersaglio={inMemoria} />
            </div>
          )}

          {v === 'conversazioni' && (
            <div className="ast-bd ast-conversazioni">
              <ConversazioniVista attuale={conversazioneId} mobile={mobile} onApri={apriConversazione} onEliminata={eliminata} />
            </div>
          )}

          {v === 'azioni' && (
            <div className="ast-bd ast-azioni">
              {mobile ? <AzioniAssistenteMobile onLink={apriLink} /> : <AzioniAssistente />}
            </div>
          )}

          <div
            className="ast-bd"
            ref={corpo}
            hidden={v !== 'chat'}
            onScroll={(e) => {
              const c = e.currentTarget;
              const prima = ultimoScroll.current;
              // Chi legge torna su: lo scorrimento smette di seguire la risposta. Non quando è il
              // contenuto ad accorciarsi (la riga di stato che sparisce porta su lo scorrimento da sola).
              if (c.scrollTop < prima.top - 2 && c.scrollHeight >= prima.altezza) segui.current = false;
              if (c.scrollHeight - c.scrollTop - c.clientHeight < 48) segui.current = true;
              ultimoScroll.current = { top: c.scrollTop, altezza: c.scrollHeight };
              scrollChat.current = c.scrollTop;
            }}
          >
            {vuota ? (
              <div className="ast-vuota">
                <button className="ast-mascotte" onClick={toccaMascotte} aria-label="Tocca la mascotte">
                  <Mascotte size={80} stato={faccia} occhi={occhi} costume={costume} />
                </button>
                <div className="ast-ciao">Ciao {user?.firstName ?? ''}</div>
                {/* Dalla 2F anche le foto e i PDF (la frase sta in allegatiTesto.ts). */}
                <p className="ast-spiega">{fermo ?? (conVocali ? PAROLE_VOCALI.vuoto : PAROLE_ALLEGATI.vuoto)}</p>
                {(pronto || caricando) && (
                  <div className="ast-sugg">
                    {SUGGERIMENTI.map((s) => (
                      <button key={s} className="ast-sugg-btn" onClick={() => { setTesto(s); if (colMouse()) campo.current?.focus(); }}>
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="ast-msgs">
                {messaggi.map((m, i) => (
                  <div
                    key={m.id}
                    className="ast-msg-blocco"
                    data-ruolo={m.ruolo}
                    // Entra solo la domanda appena mandata, non tutte a ogni rimontaggio.
                    data-entra={m.ruolo === 'utente' && i === messaggi.length - 2 && messaggi[i + 1]?.inArrivo ? 'true' : undefined}
                    data-reazione={m.reazione ? 'true' : undefined}
                  >
                    {m.ruolo === 'assistente' ? (
                      m.errore && !m.parti?.length ? (
                        <Fumetto m={m} ultima={i === messaggi.length - 1} faccia={faccia} occhi={occhi} costume={costume} />
                      ) : (
                        <Risposta
                          m={m}
                          ultima={i === messaggi.length - 1}
                          faccia={faccia}
                          occhi={occhi}
                          costume={costume}
                          scorre={corpo}
                          segui={segui}
                          attesaLunga={attesaLunga}
                          mobile={mobile}
                          onLink={apriLink}
                          cartaInBarra={cartaUltima?.id ?? null}
                        />
                      )
                    ) : (
                      <>
                        {/* Le foto e i PDF della domanda sopra il fumetto del testo, che c'è solo se
                            c'è del testo (2F): una domanda può essere fatta di soli allegati. La
                            reazione di MUNE sta sull'angolo del fumetto del testo; senza testo
                            sull'ultimo allegato (dalla revisione, 29/10/2026): il blocco, con gli
                            allegati, è largo quanto la loro riga, e il tondo finiva lontano. */}
                        {!!m.allegati?.length && (
                          <AllegatiDomanda
                            allegati={m.allegati}
                            mobile={mobile}
                            onApri={apriFotoGrande}
                            reazione={m.testo ? null : reazioneDi(m.reazione)}
                          />
                        )}
                        {(m.testo || !m.allegati?.length) && (
                          <div className="ast-fumetto">
                            <div className="ast-msg" data-ruolo="utente">{m.testo}</div>
                            {reazioneDi(m.reazione)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sempre montata: i lettori di schermo annunciano i cambi dentro una regione che c'è già. */}
          <div className="sr-only" role="status" aria-live="polite">{daAnnunciare}</div>

          {/* Al telefono, con una carta da decidere, la barra dei suoi tasti prende il posto del
              campo (2D), e dalla 2F anche della striscia degli allegati. Il campo resta montato e
              nascosto: quello che c'era scritto e gli allegati non si perdono. */}
          {cartaInBarra && <BarraCarta carta={cartaInBarra} />}

          <form ref={piede} className="ast-ft" hidden={v !== 'chat' || conBarra} onSubmit={(e) => { e.preventDefault(); invia(); }}>
            {/* Sopra il campo le foto e i PDF da mandare, con la riga rossa dei loro errori (2F). */}
            <StrisciaAllegati campo={campoAllegati} mobile={mobile} onNuovaConversazione={nuova} />
            {/* «Trascrivo il vocale…» finché il testo non arriva nel campo (2G). */}
            <RigaTrascrivo inCoda={vocali.inCoda} />
            <div className="ast-ft-riga">
              {/* La graffetta c'è solo quando il campo si può usare: MUNE spento, niente graffetta.
                  Coi vocali accesi fa scegliere anche i file audio (2G); mentre si registra no. */}
              {(pronto || caricando) && vocali.fase === 'fermo' && (
                <GraffettaAllegati mobile={mobile} onFile={sceltiFile} piu={conVocali ? ACCETTA_AUDIO : undefined} />
              )}
              {/* Mentre si registra la barra prende il posto del campo, che resta montato (2G). */}
              {vocali.fase !== 'fermo' && <BarraRegistrazione vocali={vocali} mobile={mobile} />}
              <textarea
                ref={campo}
                className="ast-campo"
                hidden={vocali.fase !== 'fermo'}
                rows={1}
                value={testo}
                maxLength={MAX_MESSAGGIO}
                disabled={!pronto && !caricando}
                placeholder={pronto || caricando ? 'Scrivi una richiesta…' : 'MUNE non disponibile'}
                onChange={(e) => {
                  setTesto(e.target.value);
                  setOcchiX(terzoDelCursore(e.target));
                  // Mentre scrivi la mascotte guarda il campo; un secondo e mezzo dopo smette.
                  setScrive(true);
                  window.clearTimeout(smettiDiScrivere.current);
                  smettiDiScrivere.current = window.setTimeout(() => setScrive(false), 1500);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    invia();
                  }
                }}
                // Uno screenshot con Ctrl+V diventa un allegato (2F); col testo negli appunti si
                // incolla il testo come prima, anche quando Excel o Word ci mettono un'immagine.
                onPaste={(e) => {
                  const file = fileDaIncollare(e);
                  if (!file) return;
                  const altri = smista(file);
                  if (altri.length) campoAllegati.aggiungi(altri, 'incolla');
                }}
              />
              {/* Il microfono (2G): al computer sempre, per dettare anche in coda a un testo; al
                  telefono al posto di Invia quando il campo è vuoto, come su WhatsApp. */}
              {conVocali && vocali.fase === 'fermo' && (!mobile || !testo.trim()) && <MicrofonoVocale vocali={vocali} />}
              {/* Acceso anche senza testo con almeno un allegato (2F); spento mentre un vocale si
                  trascrive (2G). Al telefono, coi vocali, c'è quando c'è qualcosa da mandare. */}
              {vocali.fase === 'fermo' && (!mobile || !conVocali || !!testo.trim() || campoAllegati.allegati.length > 0) && (
                <button
                  type="submit"
                  className="ast-invia"
                  disabled={(!testo.trim() && !campoAllegati.allegati.length) || inAttesa || !pronto || vocali.inCoda > 0}
                  aria-label="Invia"
                  title="Invia (Invio)"
                >
                  <Icon name="send" size={14} />
                </button>
              )}
            </div>
          </form>

          {/* La foto grande di un fumetto (2F): al computer copre corpo e piede, al telefono tutto il pannello. */}
          {fotoAperta && <FotoGrande allegato={fotoAperta} mobile={mobile} onChiudi={chiudiFotoGrande} />}
          {/* Mentre sopra il pannello passa un file (computer). */}
          {trascina && <RilascioAllegati />}
        </div>
      </div>
    </aside>
  );
}
